/* eslint-disable no-param-reassign */
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

import type { AppliedSnapBatch } from 'components/route-manager/BulkActions/BulkChangeContainerLocationDialog/snap/appliedBatchStorage'
import type { SnapToRoadApplyBody, SnapToRoadApplyOverride } from 'types/api/container/snapToRoadRequests'
import type { LatLng } from 'types/modules/container/snapToRoad'

// Client state for the snap-to-road flow inside the Change-container-location
// dialog. Server state (batch progress, staged results) stays in RTK Query —
// this slice holds only what the reviewer is doing.
//
// The review is deliberately modeled as the same delta the apply endpoint takes:
// everything in a batch is accepted-as-snapped unless the reviewer rejected it
// or dragged it. Storing rejections and placements separately (rather than one
// decision-per-item map) means unchecking a row can't discard a drag the
// reviewer already made — re-checking restores it.

export type SnapReviewFilter = 'all' | 'moving' | 'flagged' | 'failed'

export type SnapPhase = 'idle' | 'processing' | 'review'

/** Where a placement came from. `drag` is the only one the reviewer did by hand. */
export type SnapPlacementSource = 'stop' | 'driverPath' | 'drag'

/**
 * Which list the dialog's left pane shows. `stops` is the shipped stop list and
 * the default; its serial chips select containers directly, but only the four a
 * stop shows before the `+N` overflow. `containers` is the complete list, so it
 * is the only way to reach the fifth container on a stop.
 */
export type ContainerListMode = 'stops' | 'containers'

export type SnapToRoadState = {
  listMode: ContainerListMode
  phase: SnapPhase
  // The batch being computed or reviewed; null outside a run.
  batchId: string | null
  /**
   * The route and day the batch was started for. A run now survives closing the
   * dialog, and reopening goes back through the route picker — so without these a
   * review staged for one route would be drawn over another route's containers.
   */
  runRouteId: string | null
  runServiceDate: string | null
  // Containers picked in the idle surface, the input to the next preview.
  selectedContainerIds: string[]
  /**
   * Whether a scope has been established for the containers currently on screen.
   *
   * An empty `selectedContainerIds` is two different situations — nobody has
   * chosen yet, and the reviewer unchecked everything — and seeding has to tell
   * them apart. Without this it re-selects the whole route whenever the container
   * list is rebuilt, and highlighting a marker rebuilds it, so one map click put
   * back everything a deliberate clear had just removed.
   */
  isScopeSeeded: boolean
  // Unchecked rows — "keep the original location".
  rejectedItemIds: string[]
  // Reviewer placements, keyed by itemId (not containerId — the apply endpoint
  // keys on itemId).
  manualPlacements: Record<string, LatLng>
  // How each placement was made. Calling a one-click move to the stop "placed by
  // hand" is a small lie that costs trust — the reviewer knows they did not drag
  // anything.
  placementSources: Record<string, SnapPlacementSource>
  /**
   * Failures ticked for a bulk placement. A failure has no proposal to accept, so
   * its checkbox cannot mean "include this in the update" the way a normal row's
   * does — it means "this is one of the ones I'm about to place". Once placed the
   * item becomes an ordinary update row and drops out of here.
   */
  selectedForPlacementItemIds: string[]
  reviewFilter: SnapReviewFilter
  /**
   * The row the map is marking. Keyed by itemId where there is one and by
   * containerId otherwise — a container the server auto-applied has no review
   * item, and it still has to be inspectable, because "no review needed" is a
   * claim the reviewer should be able to check.
   */
  focusedRowId: string | null
  /**
   * The row the map is *framing*, deliberately not the one it is marking: selecting
   * should point at a container, not fly the map away from it. Only the crosshair and
   * a placement — which moves a container out of view — ask for a frame.
   */
  framedRowId: string | null
  /**
   * How far a container has to move before the review flags it. A viewing lens
   * rather than a decision — it never changes what apply writes — so it survives
   * a Start over the way the list mode does.
   */
  flagThresholdFt: number
  /**
   * The destination the run was started for, when it was not the road.
   *
   * A placement is keyed by `itemId`, which only exists once the batch has been
   * created — so choosing "driver path" up front cannot place anything yet. The
   * intent is parked here and applied to every item the moment the results land,
   * which is what stops the reviewer having to snap to road first and override
   * afterwards. Cleared as soon as it has been spent.
   */
  pendingPlacementSource: SnapPlacementSource | null
  /**
   * The batch this browser last wrote, kept so it can still be undone. Deliberately
   * outlives everything else here, because a new run replaces `batchId`. Only a
   * successful revert, a dismissal, or the stored entry expiring takes it away.
   */
  appliedBatch: AppliedSnapBatch | null
}

/** Matches the service's own BIG_MOVE boundary, so a fresh run opens where the server would draw the line. */
export const DEFAULT_FLAG_THRESHOLD_FT = 100

const EMPTY_IDS: string[] = Object.freeze([]) as unknown as string[]
const EMPTY_PLACEMENTS: Record<string, LatLng> = Object.freeze({}) as Record<string, LatLng>
const EMPTY_SOURCES: Record<string, SnapPlacementSource> = Object.freeze({}) as Record<string, SnapPlacementSource>

const initialState: SnapToRoadState = {
  listMode: 'stops',
  phase: 'idle',
  batchId: null,
  runRouteId: null,
  runServiceDate: null,
  selectedContainerIds: [],
  isScopeSeeded: false,
  rejectedItemIds: [],
  manualPlacements: {},
  reviewFilter: 'all',
  focusedRowId: null,
  framedRowId: null,
  placementSources: {},
  selectedForPlacementItemIds: [],
  flagThresholdFt: DEFAULT_FLAG_THRESHOLD_FT,
  pendingPlacementSource: null,
  appliedBatch: null,
}

const snapToRoadSlice = createSlice({
  name: 'snapToRoad',
  initialState: { ...initialState },
  reducers: {
    // Both lists select containers — chips in the stop list, checkboxes in the
    // container list — so the mode is a lens on one selection, not a scope of
    // its own. Switching must never silently drop what the user picked.
    setSnapListMode: (state, action: PayloadAction<ContainerListMode>) => {
      state.listMode = action.payload
    },
    // Every explicit scope write settles the question seeding asks, including the
    // master checkbox clearing the list: an empty scope somebody chose is still a
    // scope, and re-filling it is the one thing seeding must not do.
    setSnapSelectedContainers: (state, action: PayloadAction<string[]>) => {
      state.selectedContainerIds = action.payload
      state.isScopeSeeded = true
    },
    // Lasso unions with the existing selection rather than replacing it.
    addSnapSelectedContainers: (state, action: PayloadAction<string[]>) => {
      state.selectedContainerIds = Array.from(new Set([...state.selectedContainerIds, ...action.payload]))
    },
    toggleSnapContainerSelected: (state, action: PayloadAction<string>) => {
      const id = action.payload
      state.selectedContainerIds = state.selectedContainerIds.includes(id)
        ? state.selectedContainerIds.filter(selected => selected !== id)
        : [...state.selectedContainerIds, id]
    },
    // Mirrors `setSnapSelectedContainers([])` — clearing is a choice, so it settles
    // the seeding question too. Without that, seeding reads the empty scope as
    // unseeded and refills the whole route.
    clearSnapSelection: state => {
      state.selectedContainerIds = []
      state.isScopeSeeded = true
    },
    // A new run always starts from a clean review — decisions belong to a batch.
    // `placementSource` records a destination other than the road, to be applied
    // to every item once the results arrive.
    snapPreviewStarted: (
      state,
      action: PayloadAction<{ batchId: string; routeId: string; serviceDate: string; placementSource?: SnapPlacementSource }>
    ) => {
      state.phase = 'processing'
      state.batchId = action.payload.batchId
      state.runRouteId = action.payload.routeId
      state.runServiceDate = action.payload.serviceDate
      state.rejectedItemIds = []
      state.manualPlacements = {}
      state.placementSources = {}
      state.selectedForPlacementItemIds = []
      state.reviewFilter = 'all'
      state.focusedRowId = null
      state.framedRowId = null
      state.pendingPlacementSource = action.payload.placementSource ?? null
    },
    snapPreviewReady: state => {
      state.phase = 'review'
    },
    setSnapReviewFilter: (state, action: PayloadAction<SnapReviewFilter>) => {
      state.reviewFilter = action.payload
    },
    setSnapFocusedRow: (state, action: PayloadAction<string | null>) => {
      state.focusedRowId = action.payload
    },
    // The crosshair marks as well as frames — framing a container the list is not
    // pointing at leaves the reviewer looking at an unmarked pin.
    setSnapFramedRow: (state, action: PayloadAction<string | null>) => {
      state.focusedRowId = action.payload
      state.framedRowId = action.payload
    },
    setSnapFlagThreshold: (state, action: PayloadAction<number>) => {
      state.flagThresholdFt = action.payload
    },
    // Checked = update this location. Unchecking records a rejection; re-checking
    // clears it and leaves any manual placement intact.
    setSnapItemIncluded: (state, action: PayloadAction<{ itemId: string; included: boolean }>) => {
      const { itemId, included } = action.payload
      state.rejectedItemIds = included
        ? state.rejectedItemIds.filter(rejected => rejected !== itemId)
        : Array.from(new Set([...state.rejectedItemIds, itemId]))
    },
    setSnapPlacementSelected: (state, action: PayloadAction<{ itemIds: string[]; selected: boolean }>) => {
      const { itemIds, selected } = action.payload
      state.selectedForPlacementItemIds = selected
        ? Array.from(new Set([...state.selectedForPlacementItemIds, ...itemIds]))
        : state.selectedForPlacementItemIds.filter(id => !itemIds.includes(id))
    },
    setSnapItemsIncluded: (state, action: PayloadAction<{ itemIds: string[]; included: boolean }>) => {
      const { itemIds, included } = action.payload
      state.rejectedItemIds = included
        ? state.rejectedItemIds.filter(rejected => !itemIds.includes(rejected))
        : Array.from(new Set([...state.rejectedItemIds, ...itemIds]))
    },
    // Dragging a pin. On a failure this is the only thing that makes the item
    // applicable at all, so it also clears any rejection.
    setSnapManualPlacement: (state, action: PayloadAction<{ itemId: string; location: LatLng; source: SnapPlacementSource }>) => {
      const { itemId, location, source } = action.payload
      state.manualPlacements[itemId] = location
      state.placementSources[itemId] = source
      state.rejectedItemIds = state.rejectedItemIds.filter(rejected => rejected !== itemId)
      state.selectedForPlacementItemIds = state.selectedForPlacementItemIds.filter(selected => selected !== itemId)
      // The map follows a placement — otherwise the container lands somewhere off
      // screen and the reviewer has to hunt for what they just did. Not on a drag:
      // there the reviewer is already looking at the pin, and re-framing under
      // their cursor reads as the drag being rejected.
      if (source !== 'drag') {
        state.focusedRowId = itemId
        state.framedRowId = itemId
      }
    },
    /**
     * Many placements at once — a bulk action, or the destination chosen before the
     * run. One dispatch rather than one per container, because these run over the
     * whole selection and a hundred dispatches is a hundred renders.
     *
     * Does not move the map: framing one container out of a hundred would pick an
     * arbitrary winner.
     */
    setSnapManualPlacements: (state, action: PayloadAction<{ placements: Record<string, LatLng>; source: SnapPlacementSource }>) => {
      const { placements, source } = action.payload
      const itemIds = Object.keys(placements)
      if (itemIds.length === 0) return
      itemIds.forEach(itemId => {
        state.manualPlacements[itemId] = placements[itemId]
        state.placementSources[itemId] = source
      })
      state.rejectedItemIds = state.rejectedItemIds.filter(rejected => !placements[rejected])
      state.selectedForPlacementItemIds = state.selectedForPlacementItemIds.filter(selected => !placements[selected])
    },
    clearSnapManualPlacement: (state, action: PayloadAction<string>) => {
      delete state.manualPlacements[action.payload]
      delete state.placementSources[action.payload]
    },
    // Spent as soon as the results land and the destination has been applied.
    snapPendingPlacementCleared: state => {
      state.pendingPlacementSource = null
    },
    // The write landed. Recorded rather than announced-and-forgotten, because the
    // batch id is the only handle the revert endpoint takes.
    snapBatchApplied: (state, action: PayloadAction<AppliedSnapBatch>) => {
      state.appliedBatch = action.payload
    },
    // Read back from storage when the dialog opens, which is the only way an
    // Undo survives a reload.
    snapAppliedBatchHydrated: (state, action: PayloadAction<AppliedSnapBatch | null>) => {
      state.appliedBatch = action.payload
    },
    snapAppliedBatchCleared: state => {
      state.appliedBatch = null
    },
    /**
     * Footer "Reset" — discard the run and its decisions, keep the scope. The point
     * of resetting is to run again on the same containers, so clearing the
     * selection too would leave the run button disabled with nothing on screen
     * explaining why, and sending the reviewer back to the route picker would make
     * Reset indistinguishable from Cancel.
     */
    snapRunDiscarded: state => ({
      ...initialState,
      listMode: state.listMode,
      selectedContainerIds: state.selectedContainerIds,
      // Travels with the selection it describes — dropping it would have seeding
      // overwrite the scope Reset just kept.
      isScopeSeeded: state.isScopeSeeded,
      flagThresholdFt: state.flagThresholdFt,
      appliedBatch: state.appliedBatch,
    }),
    // Neither the threshold nor an unspent Undo is run state — and closing the dialog
    // is how you get back to the map to notice the apply was wrong.
    resetSnapFlow: state => ({ ...initialState, flagThresholdFt: state.flagThresholdFt, appliedBatch: state.appliedBatch }),
  },
})

export const {
  setSnapListMode,
  setSnapSelectedContainers,
  addSnapSelectedContainers,
  toggleSnapContainerSelected,
  clearSnapSelection,
  snapPreviewStarted,
  snapPreviewReady,
  setSnapReviewFilter,
  setSnapFocusedRow,
  setSnapFramedRow,
  setSnapFlagThreshold,
  setSnapItemIncluded,
  setSnapItemsIncluded,
  setSnapPlacementSelected,
  setSnapManualPlacement,
  setSnapManualPlacements,
  clearSnapManualPlacement,
  snapPendingPlacementCleared,
  snapBatchApplied,
  snapAppliedBatchHydrated,
  snapAppliedBatchCleared,
  snapRunDiscarded,
  resetSnapFlow,
} = snapToRoadSlice.actions

export const selectSnapListMode = (state: unknown): ContainerListMode =>
  (get(state, 'snapToRoad.listMode') as ContainerListMode) ?? initialState.listMode

export const selectSnapPhase = (state: unknown): SnapPhase => (get(state, 'snapToRoad.phase') as SnapPhase) ?? initialState.phase

export const selectSnapBatchId = (state: unknown): string | null =>
  (get(state, 'snapToRoad.batchId') as string | null) ?? initialState.batchId

export const selectSnapRunRouteId = (state: unknown): string | null =>
  (get(state, 'snapToRoad.runRouteId') as string | null) ?? initialState.runRouteId

export const selectSnapRunServiceDate = (state: unknown): string | null =>
  (get(state, 'snapToRoad.runServiceDate') as string | null) ?? initialState.runServiceDate

export const selectSnapSelectedContainerIds = (state: unknown): string[] =>
  (get(state, 'snapToRoad.selectedContainerIds') as string[]) ?? EMPTY_IDS

export const selectSnapIsScopeSeeded = (state: unknown): boolean =>
  (get(state, 'snapToRoad.isScopeSeeded') as boolean) ?? initialState.isScopeSeeded

export const selectSnapRejectedItemIds = (state: unknown): string[] => (get(state, 'snapToRoad.rejectedItemIds') as string[]) ?? EMPTY_IDS

export const selectSnapManualPlacements = (state: unknown): Record<string, LatLng> =>
  (get(state, 'snapToRoad.manualPlacements') as Record<string, LatLng>) ?? EMPTY_PLACEMENTS

export const selectSnapSelectedForPlacementItemIds = (state: unknown): string[] =>
  (get(state, 'snapToRoad.selectedForPlacementItemIds') as string[]) ?? EMPTY_IDS

export const selectSnapPlacementSources = (state: unknown): Record<string, SnapPlacementSource> =>
  (get(state, 'snapToRoad.placementSources') as Record<string, SnapPlacementSource>) ?? EMPTY_SOURCES

export const selectSnapReviewFilter = (state: unknown): SnapReviewFilter =>
  (get(state, 'snapToRoad.reviewFilter') as SnapReviewFilter) ?? initialState.reviewFilter

export const selectSnapFocusedRowId = (state: unknown): string | null =>
  (get(state, 'snapToRoad.focusedRowId') as string | null) ?? initialState.focusedRowId

export const selectSnapFramedRowId = (state: unknown): string | null =>
  (get(state, 'snapToRoad.framedRowId') as string | null) ?? initialState.framedRowId

export const selectSnapFlagThresholdFt = (state: unknown): number =>
  (get(state, 'snapToRoad.flagThresholdFt') as number) ?? DEFAULT_FLAG_THRESHOLD_FT

export const selectSnapPendingPlacementSource = (state: unknown): SnapPlacementSource | null =>
  (get(state, 'snapToRoad.pendingPlacementSource') as SnapPlacementSource | null) ?? null

export const selectSnapAppliedBatch = (state: unknown): AppliedSnapBatch | null =>
  (get(state, 'snapToRoad.appliedBatch') as AppliedSnapBatch | null) ?? null

/**
 * The apply payload. A rejection outranks a placement — the reviewer unchecked
 * the row after dragging it — and the backend resolves rejections first anyway,
 * so a rejected item never ships an override.
 */
export const selectSnapApplyDelta = createSelector(
  [selectSnapRejectedItemIds, selectSnapManualPlacements],
  (rejectedItemIds, manualPlacements): SnapToRoadApplyBody => {
    const overrides: SnapToRoadApplyOverride[] = Object.entries(manualPlacements)
      .filter(([itemId]) => !rejectedItemIds.includes(itemId))
      .map(([itemId, location]) => ({ itemId, latitude: location.latitude, longitude: location.longitude }))

    return { rejectedItemIds: [...rejectedItemIds], overrides }
  }
)

export default snapToRoadSlice.reducer
